import { fmtPrice as quoteMoney42 } from '@/lib/api';
import { useLocale as useLocaleR42 } from '@/lib/locale';
import { toMajor as moneyToMajor42 } from '@/lib/region';
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Lock, CheckCircle2, AlertTriangle, BadgeDollarSign, TicketPercent, X, HandCoins } from 'lucide-react';
import PayAnotherWayOption from '@/components/PayAnotherWayOption';
import { handleStripeDecline } from '@/lib/paymentDecline';
import { PaymentDeclineNotice } from '@/components/PaymentDeclineNotice';

/**
 * ApplicationFeeStep (Phase 87)
 * -----------------------------
 * Apply-time Registration fee / Assessment step in the parent APPLICATION
 * wizard. Patterned on PaymentStep.jsx but intentionally simpler:
 *
 *   * ALWAYS a SetupIntent — the card is SAVED here, never charged. The
 *     coach's approval is the charge moment (instant for Instant Signup
 *     coaches). Declined applications are never charged.
 *   * The server decides whether a fee is even due (scope=first_only
 *     returning families, Stripe not connected, fee disabled → `fee_due:
 *     false`) — in that case we auto-advance via onSkip().
 *
 * Props:
 *   coachSlug, coachDisplayName — connected-account context + copy
 *   parent: {email, first_name, last_name, phone}
 *   studentCount: number — per-child fees multiply by this
 *   publishableKey: platform Stripe publishable key
 *   onSuccess({setup_intent_id, fee_discount_code?}) — parent stores it in the register payload
 *   onSkip(reason) — no fee due; wizard advances past this step
 *   onWaived({code_applied, ...}) — Phase 98: a 100% registration-fee code
 *     dropped the fee to $0; no card needed. Parent records the code so
 *     /register can re-validate + mark the application `waived_code`.
 *   payAnotherWay — Phase 99: {enabled, instructions} from the coach's
 *     public settings. When enabled, an "Or pay another way" option lets
 *     the parent skip the card entirely (Venmo/Zelle/cash); the fee lands
 *     as pending_external and the coach confirms receipt manually.
 *   onExternal({amount_cents, label}) — parent confirmed the offline
 *     choice; wizard records application_fee_external=true and advances.
 */

export function ApplicationFeeStep({ coachSlug, coachDisplayName, parent, studentCount, publishableKey, onSuccess, onSkip, onWaived, payAnotherWay, onExternal }) {
    const { t: t42 } = useLocaleR42();
    const [initData, setInitData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    // Phase 98 — registration-fee discount code state.
    const [codeInput, setCodeInput] = useState('');
    const [codeError, setCodeError] = useState('');
    const [applying, setApplying] = useState(false);

    const runInit = async (discountCode) => {
        const r = await api.post(`/public/coach/${coachSlug}/application-fee/init`, {
            parent_email: parent.email,
            parent_first_name: parent.first_name,
            parent_last_name: parent.last_name,
            parent_phone: parent.phone || '',
            student_count: studentCount || 1,
            discount_code: discountCode || undefined,
        });
        return r.data || {};
    };

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        runInit('')
            .then((data) => {
                if (!alive) return;
                if (!data.fee_due) {
                    onSkip?.(data.reason || 'not_due');
                    return;
                }
                setInitData(data);
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load the fee step'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coachSlug, parent.email, studentCount]);

    // Apply/remove a code — re-runs init so the SetupIntent + displayed
    // amount always come from the server (the client never does fee math).
    const applyCode = async (raw) => {
        const code = (raw || '').trim();
        setApplying(true);
        setCodeError('');
        try {
            const data = await runInit(code);
            if (!data.fee_due) {
                if (data.reason === 'code_waived') {
                    onWaived?.(data);
                } else {
                    onSkip?.(data.reason || 'not_due');
                }
                return;
            }
            if (code && data.code_error) {
                setCodeError(data.code_error);
            }
            setInitData(data);
        } catch (e) {
            setCodeError(e?.response?.data?.detail || 'Could not check that code. Try again.');
        } finally {
            setApplying(false);
        }
    };

    const stripePromise = useMemo(() => {
        if (!publishableKey || !initData?.stripe_account_id) return null;
        return loadStripe(publishableKey, { stripeAccount: initData.stripe_account_id });
    }, [publishableKey, initData?.stripe_account_id]);

    if (loading) {
        return (
            <Card className="sd-card flex items-center gap-2 p-6 text-sm text-[#1a3a5c]/70" data-testid="feestep-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> {t42("Checking fee details…")} </Card>
        );
    }
    if (error) {
        return (
            <Card className="sd-card border-[#ff6b35]/40 p-6" data-testid="feestep-error">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#ff6b35]" />
                    <div>
                        <div className="font-semibold text-[#0a1628]">{t42("Couldn't load this step")}</div>
                        <p className="mt-1 text-sm text-[#1a3a5c]">{error}</p>
                    </div>
                </div>
            </Card>
        );
    }
    if (!stripePromise || !initData?.client_secret) {
        return (
            <Card className="sd-card p-6" data-testid="feestep-not-ready">
                <p className="text-sm text-[#1a3a5c]">{t42("This step isn't available right now. Please try again later.")}</p>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {/* Phase 98 — registration-fee discount code. Applied codes
                re-mint the SetupIntent server-side; the key on <Elements>
                forces a clean remount with the new client_secret. */}
            <Card className="sd-card p-4" data-testid="feestep-code-box">
                {initData.code_applied ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <div className="flex items-center gap-2 font-medium text-[#0a1628]">
                            <TicketPercent className="h-4 w-4 text-[#00d4ff]" /> {t42("Code")} <strong>{initData.code_applied}</strong> {t42("applied — you save")}{' '}
                            <strong>{quoteMoney42(initData.discount_cents || 0, initData.currency)}</strong>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setCodeInput(''); applyCode(''); }} disabled={applying} data-testid="feestep-code-remove">
                            <X className="mr-1 h-3.5 w-3.5" /> {t42("Remove")} </Button>
                    </div>
                ) : (
                    <div>
                        <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-[#0a1628]">
                            <TicketPercent className="h-4 w-4 text-[#00d4ff]" /> {t42("Have a code from your coach?")} </div>
                        <div className="flex gap-2">
                            <Input
                                value={codeInput}
                                onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeError(''); }}
                                placeholder={t42("e.g. FAMILY2026")}
                                className="h-10 rounded-xl uppercase"
                                data-testid="feestep-code-input"
                            />
                            <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => applyCode(codeInput)} disabled={applying || !codeInput.trim()} data-testid="feestep-code-apply">
                                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : t42("Apply")}
                            </Button>
                        </div>
                        {codeError && <p className="mt-1.5 text-xs text-[#ff6b35]" data-testid="feestep-code-error">{codeError}</p>}
                    </div>
                )}
            </Card>

            <Elements
                key={initData.client_secret}
                stripe={stripePromise}
                options={{
                    clientSecret: initData.client_secret,
                    appearance: {
                        theme: 'stripe',
                        variables: {
                            colorPrimary: '#00d4ff',
                            colorBackground: '#ffffff',
                            colorText: '#0a1628',
                            colorDanger: '#ff6b35',
                            fontFamily: 'Figtree, system-ui, sans-serif',
                            borderRadius: '12px',
                        },
                    },
                }}
            >
                <FeeInner initData={initData} coachDisplayName={coachDisplayName} onSuccess={onSuccess} coachSlug={coachSlug} parent={parent} />
            </Elements>

            {/* Phase 99 — offline fee payment (Venmo/Zelle/cash). Only when
                the coach opted in. The fee lands as pending_external and the
                coach confirms receipt via "Mark fee received". */}
            {payAnotherWay?.enabled && (
                <PayAnotherWayOption
                    idPrefix="feestep"
                    instructions={payAnotherWay.instructions}
                    coachDisplayName={coachDisplayName}
                    onConfirm={() => onExternal?.({
                        amount_cents: initData.amount_cents || 0,
                        label: initData.label || 'Registration fee',
                    })}
                />
            )}
        </div>
    );
}

function FeeInner({ initData, coachDisplayName, onSuccess, coachSlug, parent }) {
    const { t: t42 } = useLocaleR42();
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errMsg, setErrMsg] = useState('');
    const [decline, setDecline] = useState(null);
    const tryAnotherCard = () => {
        setDecline(null); setErrMsg('');
        try { elements?.getElement('payment')?.clear(); } catch { /* noop */ }
        try { elements?.getElement('payment')?.focus(); } catch { /* noop */ }
    };

    const amount = initData.amount_cents || 0;
    const currency = initData.currency || 'usd';
    const label = initData.label || 'Registration fee';
    const isAssessment = initData.fee_type === 'assessment';
    const perChild = initData.per === 'child' && (initData.student_count || 1) > 1;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setErrMsg('');
        setDecline(null);
        const result = await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });
        if (result.error) {
            setDecline(handleStripeDecline(result.error, {
                context: 'application_fee',
                coachSlug,
                amountCents: initData?.amount_cents || 0,
                packageName: initData?.label || 'Registration fee',
                parentEmail: parent?.email,
                parentName: [parent?.first_name, parent?.last_name].filter(Boolean).join(' ') || undefined,
            }));
            setSubmitting(false);
            return;
        }
        onSuccess({
            setup_intent_id: result.setupIntent?.id,
            // Phase 98 — carry the applied fee code so /register re-validates
            // it and stores the discount on the application row.
            fee_discount_code: initData.code_applied || undefined,
        });
        setSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="feestep-form">
            <Card className="sd-card p-4" data-testid="feestep-summary">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div className="flex items-start gap-2.5">
                        <div className="mt-0.5 rounded-lg bg-[#00d4ff]/10 p-1.5">
                            <BadgeDollarSign className="h-4 w-4 text-[#0a1628]" />
                        </div>
                        <div>
                            <div className="font-semibold text-[#0a1628]" data-testid="feestep-label">{label}</div>
                            <div className="text-xs text-[#1a3a5c]/70">
                                {perChild && `${quoteMoney42(initData.amount_cents / (initData.student_count || 1), initData?.currency || 'usd')} × ${initData.student_count} kids · `}
                                {isAssessment
                                    ? t42("Your coach will arrange the assessment time with you.")
                                    : t42("One-time fee for joining this program.")}
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs uppercase tracking-wide text-[#1a3a5c]/60">{t42("Charged on approval")}</div>
                        <div className="font-display text-2xl font-bold text-[#0a1628]" data-testid="feestep-amount">
                            {initData.discount_cents > 0 && (
                                <span className="mr-2 text-base font-normal text-[#1a3a5c]/50 line-through" data-testid="feestep-base-amount">
                                    {quoteMoney42(initData.base_amount_cents || 0, initData.currency)}
                                </span>
                            )}
                            {`${quoteMoney42(amount, currency)}`}
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="sd-card p-4">
                <PaymentElement options={{ layout: 'tabs' }} />
            </Card>

            {/* Charge-on-approval + card-on-file disclosure. Required language
                for setup_future_usage='off_session' to hold up in disputes. */}
            <div
                className="flex items-start gap-3 rounded-xl border border-[#00d4ff]/25 bg-[#e6f9fd] p-4 text-sm text-[#0a1628]"
                data-testid="feestep-consent-notice"
            >
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" aria-hidden="true" />
                <span className="leading-relaxed">
                    <strong>{t42("You won't be charged today.")}</strong>{' '}
                    <span className="text-[#1a3a5c]/85"> {t42("Your card is saved securely now, and the")} {label.toLowerCase()} {t42("of")}{' '}
                        <strong>{`${quoteMoney42(amount, currency)}`}</strong> {t42("is charged only when")}{' '}
                        {coachDisplayName || 'your coach'} {t42("approves your application. You also authorize")}{' '}
                        {coachDisplayName || 'your coach'} {t42("to charge this card for future lessons or packages — manage saved cards anytime from your parent portal.")} </span>
                </span>
            </div>

            {decline && (
                <PaymentDeclineNotice decline={decline} idPrefix="feestep-decline" onTryAnother={tryAnotherCard} onRetry={() => setDecline(null)} />
            )}
            {errMsg && !decline && (
                <div
                    className="rounded-xl border border-[#ff6b35]/40 bg-[#fff5f0] p-3 text-sm text-[#ff6b35]"
                    data-testid="feestep-error-msg"
                >
                    {errMsg}
                </div>
            )}

            <Button
                type="submit"
                disabled={!stripe || submitting}
                className="sd-cta sd-focus h-12 w-full rounded-xl text-base"
                data-testid="feestep-submit-button"
            >
                {submitting
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t42("Saving card…")}</>
                    : <><CheckCircle2 className="mr-2 h-4 w-4" /> {t42("Save card & continue")}</>}
            </Button>

            <p className="text-center text-xs text-[#1a3a5c]/60">
                <Lock className="mr-1 inline h-3 w-3" /> {t42("Secured by Stripe. Nothing is charged until your application is approved.")} </p>
        </form>
    );
}

export default ApplicationFeeStep;
